import React from 'react';
import { McDrawer } from "@maersk-global/mds-react-wrapper/components-core/mc-drawer";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import { Box, Typography, Card, CardContent, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { resolveChangesData } from '../utils';

const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

// Appearance helper functions
const getStateAppearance = (status) => {
  switch (status?.toLowerCase()) {
    case 'new':
      return 'neutral';
    case 'assess':
      return 'warning';
    case 'authorize':
      return 'warning';
    case 'scheduled':
      return 'info';
    case 'implement':
      return 'info';
    case 'review':
      return 'success';
    case 'closed':
      return 'success';
    case 'canceled':
      return 'error';
    default:
      return 'neutral';
  }
};

const getRiskAppearance = (risk) => {
  switch (risk?.toLowerCase()) {
    case 'low':
      return 'success';
    case 'moderate':
      return 'warning';
    case 'high':
      return 'error';
    default:
      return 'neutral';
  }
};

const getTypeAppearance = (type) => {
  switch (type?.toLowerCase()) {
    case 'standard':
      return 'success';
    case 'normal':
      return 'neutral';
    case 'ci/cd':
      return 'warning';
    default:
      return 'neutral';
  }
};

const getChangeTypeAppearance = (changeType) => {
  switch (changeType?.toLowerCase()) {
    case 'application':
      return 'success';
    case 'infrastructure':
      return 'info';
    default:
      return 'neutral';
  }
};

// ChangeCard component
export const ChangeCard = ({ change, onChangeNumberClick }) => {
  return (
    <Card 
      sx={{ 
        mb: 2, 
        border: '1px solid #d9d9d9', 
        boxShadow: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
      }}
      onClick={() => onChangeNumberClick(change.change_number)}
    >
      <CardContent sx={{ padding: '16px !important' }}>
        {/* Header with Change Number and Tags */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: maerskFontFamily, 
                color: '#0073ab',
                fontSize: '1rem',
                textAlign: 'left'
              }}
            >
              {change.change_number}
            </Typography>
          </Box>
          
          {/* Tags inline with change number */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'flex-start' }}>     
            <McTag
              label={change.state || 'Unknown'}
              appearance={getStateAppearance(change.state)}
              fit="small"
            />
            <McTag
              label={`${change.risk_impact} Risk` || 'Unknown'}
              appearance={getRiskAppearance(change.risk_impact)}
              fit="small"
            />
            {/* <McTag
              label={change.type || 'Unknown'}
              appearance={getTypeAppearance(change.type)}
              fit="small"
            />
            <McTag
              label={change.change_type || 'Unknown'}
              appearance={getChangeTypeAppearance(change.change_type)}
              fit="small"
            /> */}
          </Box>
        </Box>

        {/* Configuration Item - Separate Row */}
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: maerskFontFamily, 
              color: '#666', 
              fontSize: '0.9rem',
              textAlign: 'left'
            }}
          >
            {change.configuration_item || 'N/A'}
          </Typography>
        </Box>

        {/* Date Information */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: maerskFontFamily, 
              fontWeight: 500, 
              color: '#555',
              fontSize: '0.8rem'
            }}
          >
            Start: {change.planned_start_date}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: maerskFontFamily, 
              fontWeight: 500, 
              color: '#555',
              fontSize: '0.8rem'
            }}
          >
            End: {change.planned_end_date}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const ChangesDrawer = ({
  isChangesDrawerOpen,
  changesDrawerHeading,
  changesDrawerContent,
  onClose,
  apiData,
  viewType,
  selectedPlatform,
  onChangeNumberClick
}) => {
    
  return (
    <McDrawer
      open={isChangesDrawerOpen}
      closed={onClose}
    >
      <span slot="heading" style={{ color: "#0073ab", fontFamily: maerskFontFamily }}>
        {changesDrawerHeading || "Changes"}
      </span>
      <Box sx={{ mt: 1, overflowY: 'auto', pr: 1 }}>
        {changesDrawerContent}
      </Box>
      <McButton slot="footer" dialogaction="close" onClick={onClose}>
        Close
      </McButton>
    </McDrawer>
  );
};// Export the function so it can be used by the parent component
export { ChangesDrawer as default };
export const useChangesDrawer = (apiData, viewType, selectedPlatform, selectedPortfolio, applicationView, onChangeNumberClick) => {
  const handleChangesDrawerOpen = (changeType) => {
    if (!apiData) return null;

    const { changesData } = resolveChangesData({
      data: apiData,
      viewType,
      applicationView,
      platformName: selectedPlatform,
      portfolioName: selectedPortfolio
    });

    if (!changesData) return null;

    let heading = "";
    let drawerContent = null;

    if (changeType === "success") {
      const changes = changesData.success_status?.successful_changes?.changes || [];
      heading = "Successful Changes";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total: {changes.length}
          </Typography>
          {changes.length > 0 ? (
            <Box>
              {changes.map((change, index) => (
                <ChangeCard 
                  key={`success-${index}-${change.change_number}`}
                  change={change}
                  onChangeNumberClick={onChangeNumberClick}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No successful changes found.
            </Typography>
          )}
        </Box>
      );
    } else if (changeType === "failure") {
      const changes = changesData.success_status?.failed_changes?.changes || [];
      heading = "Failed Changes";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total: {changes.length}
          </Typography>
          {changes.length > 0 ? (
            <Box>
              {changes.map((change, index) => (
                <ChangeCard 
                  key={`failure-${index}-${change.change_number}`}
                  change={change}
                  onChangeNumberClick={onChangeNumberClick}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No failed changes found.
            </Typography>
          )}
        </Box>
      );
    } else if (changeType === "in progress") {
      const changes = changesData.success_status?.ongoing_status_changes?.changes || [];
      heading = "In Progress Changes";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total: {changes.length}
          </Typography>
          {changes.length > 0 ? (
            <Box>
              {changes.map((change, index) => (
                <ChangeCard 
                  key={`ongoing-${index}-${change.change_number}`}
                  change={change}
                  onChangeNumberClick={onChangeNumberClick}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No in progress changes found.
            </Typography>
          )}
        </Box>
      );
    } else if (changeType === "overdue") {
      const changes = changesData.overdue_changes?.changes || [];
      heading = "Overdue Changes";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total: {changes.length}
          </Typography>
          {changes.length > 0 ? (
            <Box>
              {changes.map((change, index) => (
                <ChangeCard 
                  key={`overdue-${index}-${change.change_number}`}
                  change={change}
                  onChangeNumberClick={onChangeNumberClick}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No overdue changes found.
            </Typography>
          )}
        </Box>
      );
    } else if (changeType === "conflicts") {
      const conflictGroups = changesData.conflicting_changes?.conflict_groups || [];
      const allConflictingChanges = changesData.conflicting_changes?.changes || [];
      heading = "Conflicting Changes";
      drawerContent = (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: "#666", 
              fontSize: '1rem', 
              fontFamily: maerskFontFamily 
            }}
          >
            Total Conflicts: {conflictGroups.length}
          </Typography>
          {conflictGroups.length > 0 ? (
            <Box>
              {conflictGroups.map((group, index) => {
                // Find the actual change objects for this conflict group
                const groupChanges = group.conflicting_changes.map(changeNumber => 
                  allConflictingChanges.find(change => change.change_number === changeNumber)
                ).filter(Boolean);

                // Get all change numbers from the group (including those without data)
                const allChangeNumbers = group.conflicting_changes || [];

                // If no changes have data available, show just a header
                if (groupChanges.length === 0) {
                  return (
                    <Box 
                      key={`conflict-group-${index}-${group.group_id}`}
                      sx={{ 
                        mb: 2, 
                        p: 2,
                        border: '1px solid #d9d9d9', 
                        borderRadius: '10px',
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography 
                          sx={{ 
                            fontFamily: maerskFontFamily,
                            fontSize: '1.1rem',
                            color: '#666'
                          }}
                        >
                          {allChangeNumbers[0] || 'Unknown'}
                        </Typography>
                        <McIcon icon="lightning" color="#d161c4"/>
                        <Typography 
                          sx={{ 
                            fontFamily: maerskFontFamily,
                            fontSize: '1.1rem',
                            color: '#666'
                          }}
                        >
                          {allChangeNumbers[1] || 'Unknown'}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mt: 1, 
                          color: "#999", 
                          fontStyle: 'italic',
                          fontFamily: maerskFontFamily 
                        }}
                      >
                        No detailed information available for these conflicting changes.
                      </Typography>
                    </Box>
                  );
                }

                // For groups with 1 or more changes having data, show as accordion
                return (
                  <Accordion 
                    key={`conflict-group-${index}-${group.group_id}`}
                    sx={{ 
                      mb: 2, 
                      border: '1px solid #d9d9d9', 
                      boxShadow: 'none',
                      borderRadius: '10px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: '0 0 16px 0',
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        borderRadius: '10px',
                        '&.Mui-expanded': {
                          borderBottomLeftRadius: '0px',
                          borderBottomRightRadius: '0px',
                        },
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          gap: 1
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography 
                          sx={{ 
                            fontFamily: maerskFontFamily,
                            fontSize: '1.1rem',
                          }}
                        >
                          {allChangeNumbers[0] || 'Unknown'}
                        </Typography>
                        <McIcon icon="lightning" color="#d161c4"/>
                        <Typography 
                          sx={{ 
                            fontFamily: maerskFontFamily,
                            fontSize: '1.1rem',
                          }}
                        >
                          {allChangeNumbers[1] || 'Unknown'}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: '16px' }}>
                      <Box>
                        {groupChanges.length > 0 && (
                          groupChanges.map((change, changeIndex) => (
                            <ChangeCard 
                              key={`conflict-detail-${index}-${changeIndex}-${change.change_number}`}
                              change={change}
                              onChangeNumberClick={onChangeNumberClick}
                            />
                          ))
                        )}
                        {groupChanges.length === 1 && allChangeNumbers.length === 2 && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mt: 2, 
                              p: 2, 
                              borderRadius: '8px',
                              border: '1px solid #d9d9d9',
                              color: "#666", 
                              fontStyle: 'italic',
                              fontFamily: maerskFontFamily 
                            }}
                          >
                            Details for {allChangeNumbers.find(num => !groupChanges.some(change => change.change_number === num)) || 'the other change'} are not available.
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ) : (
            <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
              No conflicting changes found.
            </Typography>
          )}
        </Box>
      );
    }

    return { heading, drawerContent };
  };

  return { handleChangesDrawerOpen };
};
